import * as React from 'react';

import {EmptyAddCard} from '../card/empty-add-card';
import {DraggableList} from '../draggable-row/draggable-list';
import {FlashBanner} from '../flash-banner/flash-banner';
import {Stack} from '../layout/stack/stack';
import {LoadingPage} from '../loading-page/loading-page';
import {Body} from '../typography/body';

import type {FakeWidget, Widget} from './types';
import {WidgetListItem} from './widget-list-item';

type Props = {
    widgets: Widget[];
    lockedWidgets?: FakeWidget[];
    sectionTitle: string;
    isLoading?: boolean;
    onAddWidget: () => void;
    handleReorder: (startIndex: number, endIndex: number) => void;
    handleHideToggle: (widgetId: string, isHidden: boolean) => void;
    handleEdit: (widgetId: string) => void;
    handleRemove: (widgetId: string) => void;
    isInModal?: boolean;
    maxCustomWidgets?: number;
};

export const WidgetsCustomizer = (props: Props) => {
    const {handleHideToggle, handleEdit, handleRemove} = props;

    const getSubtext = (widget: Widget) => {
        if (widget.type === 'ALL_CUSTOM_FIELDS') {
            return '*Only fields with values will be shown.';
        }

        if (widget.type === 'CHILD_LEADS') {
            return '*Only displays for parent companies.';
        }

        if (widget.customWidget) {
            const descriptor = widget.customWidget.fields.length > 1 ? 'fields' : 'field';

            return widget.customWidget.fields.length > 0
                ? `${widget.customWidget.fields.length} ${descriptor}`
                : undefined;
        }

        return undefined;
    };

    const listItems = React.useMemo(() => {
        return props.widgets.map((widget) => {
            return {
                id: widget.id,
                isActive: !widget.isHidden,
                isDisabled: widget.isHidden,
                component: (
                    <WidgetListItem
                        name={widget.name}
                        subtext={getSubtext(widget)}
                        isDisabledByAdmin={widget.isDisabledByAdmin}
                        hiddenProps={
                            widget.isRequired
                                ? undefined
                                : {
                                      isHidden: widget.isHidden,
                                      onToggleHide: () => {
                                          handleHideToggle(widget.id, !widget.isHidden);
                                      },
                                  }
                        }
                        type={widget.type}
                        editAndRemoveProps={
                            widget.isEditable
                                ? {
                                      onEdit: () => {
                                          handleEdit(widget.id);
                                      },
                                      onRemove: () => {
                                          handleRemove(widget.id);
                                      },
                                  }
                                : undefined
                        }
                    />
                ),
            };
        });
    }, [props.widgets, handleHideToggle, handleEdit, handleRemove]);

    const lockedWidgets = props.lockedWidgets || [];
    const lockedItems = lockedWidgets.map((lockedWidget) => ({
        id: lockedWidget.fakeId,
        isActive: false,
        component: (
            <WidgetListItem
                name={lockedWidget.name}
                type={lockedWidget.type}
                isDisabledByAdmin={false}
            />
        ),
    }));

    const canAddWidget = React.useMemo(() => {
        if (props.maxCustomWidgets !== undefined) {
            const numWidgets = props.widgets.filter(
                (widget) => widget.type === 'CUSTOM_CUSTOM_FIELDS'
            ).length;

            // Necessary because Flow doesn't understand that maxCustomWidgets can't be undefined
            return numWidgets < (props.maxCustomWidgets || 0);
        }

        return true;
    }, [props.maxCustomWidgets, props.widgets]);

    return (
        <Stack spacing={8} isFullWidth={true}>
            <Body isBold={true}>{props.sectionTitle}</Body>
            {props.isLoading ? (
                <LoadingPage fullHeight={false} />
            ) : (
                <>
                    <DraggableList
                        listItems={listItems}
                        handleReorder={props.handleReorder}
                        isInModal={props.isInModal}
                        showHandleWhenDisabled={true}
                    />
                    <DraggableList
                        listItems={lockedItems}
                        handleReorder={() => {}}
                        isInModal={props.isInModal}
                        disabled={true}
                    />
                    {!canAddWidget && props.maxCustomWidgets && (
                        <FlashBanner type='warning' hasBorder={true}>
                            You’ve reached the maximum of {props.maxCustomWidgets} custom field
                            widgets for your workspace. To add a new one, remove an existing widget
                            first.
                        </FlashBanner>
                    )}
                    <div className='pb-16'>
                        <EmptyAddCard
                            onClick={props.onAddWidget}
                            text='+ Add custom field widget'
                            background='off-white'
                            withoutIcon={true}
                            size='large'
                            height={40}
                            isDisabled={!canAddWidget}
                        />
                    </div>
                </>
            )}
        </Stack>
    );
};
